import React from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Registration } from '../../../types/registration';

interface RegistrationInfoCardProps {
  registration: Registration;
  parBalance: number | null;
  parLoading: boolean;
  onRegistrationUpdate: (updatedRegistration: Registration) => void;
}

const RegistrationInfoCard: React.FC<RegistrationInfoCardProps> = ({
  registration,
  parBalance,
  parLoading,
  onRegistrationUpdate
}) => {
  const handleStatusChange = async (newStatus: string) => {
    if (!registration || !newStatus) return;
    
    try {
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, { status: newStatus });
      onRegistrationUpdate({ ...registration, status: newStatus });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  const handleRegistrationTypeChange = async (newPolicy: string) => {
    if (!registration) return;

    try {
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        'accommodation.policy': newPolicy
      });

      onRegistrationUpdate({
        ...registration,
        accommodation: {
          ...registration.accommodation,
          policy: newPolicy
        }
      });
    } catch (error) {
      console.error('Error updating registration type:', error);
      alert('Failed to update registration type. Please try again.');
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-body p-3">
        <h6 className="card-title mb-3" style={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
          Primary Contact
        </h6>

        <div className="row" style={{ marginBottom: '-5px' }}>
          <div className="col-4">
            <strong>Name:</strong>
          </div>
          <div className="col-8">
            {registration?.registrationInfo?.primaryCamperName}
          </div>
        </div>
        
        <div className="row" style={{ marginBottom: '-5px' }}>
          <div className="col-4">
            <strong>Email:</strong>
          </div>
          <div className="col-8">
            {registration?.registrationInfo?.email}
          </div>
        </div>
        
        <div className="row" style={{ marginBottom: '-5px' }}>
          <div className="col-4">
            <strong>Par:</strong>
          </div>
          <div className="col-8">
            {parLoading ? (
              <span className="text-muted">Loading...</span>
            ) : (
              <span style={{ fontWeight: 'bold', color: parBalance && parBalance > 0 ? '#28a745' : '#6c757d' }}>
                {parBalance !== null ? `$${parBalance.toFixed(2)}` : '$0.00'}
              </span>
            )}
          </div>
        </div>
        
        <div className="row" style={{ marginBottom: '-5px' }}>
          <div className="col-4">
            <strong>Registration Type:</strong>
          </div>
          <div className="col-8">
            <select 
              className="form-select form-select-sm"
              value={registration?.accommodation?.policy || ''}
              onChange={(e) => handleRegistrationTypeChange(e.target.value)}
            >
              <option value="">Select type</option>
              <option value="program-staff">1. Program Staff</option>
              <option value="returning-same-spot">2. Returning Same Spot</option>
              <option value="returning-no-fixed-address">3. Returning No Fixed Address</option>
              <option value="returning-different-accommodations">4. Returning Different Accommodations</option>
              <option value="new-camper">5. New Camper</option>
              <option value="private-cottage-owner">6. Private Cottage Owner</option>
            </select>
          </div>
        </div>
        
        <div className="row" style={{ marginBottom: '-5px' }}>
          <div className="col-4">
            <strong>Requested Accommodation:</strong>
          </div>
          <div className="col-8">
            {registration?.accommodation?.firstChoice && registration?.accommodation?.secondChoice 
              ? `${registration.accommodation.firstChoice} / ${registration.accommodation.secondChoice}`
              : registration?.accommodation?.firstChoice || 'Not specified'}
          </div>
        </div>
        
        <div className="row">
          <div className="col-4">
            <strong>Status:</strong>
          </div>
          <div className="col-8">
            <select 
              className="form-select form-select-sm"
              value={registration?.status || ''}
              style={{
                backgroundColor: registration?.status === 'Assigned' ? '#d4edda' : 
                               registration?.status === 'Pending' ? '#fff3cd' : 
                               registration?.status === 'Hold' ? '#f8d7da' : 'white',
                color: registration?.status === 'Assigned' ? '#155724' : 
                       registration?.status === 'Pending' ? '#856404' : 
                       registration?.status === 'Hold' ? '#721c24' : 'black'
              }}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <option value="Assigned">Assigned</option>
              <option value="Pending">Pending</option>
              <option value="Hold">Hold</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationInfoCard;

